import React, { useRef, useState } from "react";
import {
  Camera,
  Film,
  Link2,
  Maximize2,
  Plus,
  Trash2,
  Video,
} from "lucide-react";
import { uploadToCloudinary } from "../../../utility/uploadToCloudinary";
import { usePortfolioContext } from "./PortfolioContext";
import PortfolioMediaPreview, {
  PortfolioPreviewItem,
} from "./PortfolioMediaPreview";

type MediaType = "photos" | "reels" | "videos";

const Gallery: React.FC = () => {
  const { formData, setFormData } = usePortfolioContext();

  const photoInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [reelUrl, setReelUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [preview, setPreview] = useState<PortfolioPreviewItem | null>(null);

  const photoPreviews = formData.photos ? formData.photos.slice(4) : [];
  const reels = formData.instagramReels || [];
  const videos = formData.youtubeVideos || [];

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      setFormData((prev) => ({
        ...prev,
        photos: [...(prev.photos || []), url],
      }));
    } catch (err) {
      console.error("Photo upload failed", err);
    } finally {
      setIsUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const addLink = (type: "reels" | "videos") => {
    const url = (type === "reels" ? reelUrl : videoUrl).trim();
    if (!url) return;

    if (type === "reels") {
      setFormData((prev) => ({
        ...prev,
        instagramReels: [...(prev.instagramReels || []), url],
      }));
      setReelUrl("");
    } else {
      setFormData((prev) => ({
        ...prev,
        youtubeVideos: [...(prev.youtubeVideos || []), url],
      }));
      setVideoUrl("");
    }
  };

  const removeMedia = (type: MediaType, index: number) => {
    if (type === "photos") {
      setFormData((prev) => {
        const newPhotos = [...(prev.photos || [])];
        newPhotos.splice(index + 4, 1);
        return { ...prev, photos: newPhotos };
      });
    } else if (type === "reels") {
      setFormData((prev) => {
        const newReels = [...(prev.instagramReels || [])];
        newReels.splice(index, 1);
        return { ...prev, instagramReels: newReels };
      });
    } else {
      setFormData((prev) => {
        const newVideos = [...(prev.youtubeVideos || [])];
        newVideos.splice(index, 1);
        return { ...prev, youtubeVideos: newVideos };
      });
    }
  };

  const getEmbedUrl = (url: string, type: "reels" | "videos") => {
    try {
      if (type === "videos") {
        if (url.includes("youtube.com/embed/")) return url;
        let videoId = "";
        if (url.includes("youtube.com/watch")) {
          videoId = new URL(url).searchParams.get("v") || "";
        } else if (url.includes("youtu.be/")) {
          videoId = url.split("youtu.be/")[1]?.split("?")[0] || "";
        }
        return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
      } else {
        if (url.includes("instagram.com") && !url.includes("embed")) {
          const baseUrl = url.split("?")[0].replace(/\/$/, "");
          return `${baseUrl}/embed`;
        }
        return url;
      }
    } catch (e) {
      return url;
    }
  };

  const renderGallerySection = (
    title: string,
    subtitle: string,
    type: MediaType,
    items: string[],
    visibleEmptySlots: number,
  ) => {
    const kind = type === "photos" ? "image" : type === "reels" ? "reel" : "video";
    const Icon = type === "photos" ? Camera : type === "reels" ? Film : Video;
    const placeholderLabel =
      type === "photos" ? "Photo" : type === "reels" ? "Reel" : "Video";

    return (
      <section className="portfolio-gallery-section" key={type}>
        <div className="portfolio-gallery-heading">
          <div>
            <span className="portfolio-gallery-heading-icon">
              <Icon size={20} />
            </span>
            <div>
              <h2>{title}</h2>
              <p>{subtitle}</p>
            </div>
          </div>
          <span className="portfolio-count-pill">{items.length} uploaded</span>
        </div>

        <div className={`gallery-media-grid gallery-media-grid-${kind}`}>
          {items.map((url, index) => (
            <article
              className={`gallery-media-card gallery-media-${kind}`}
              key={`${type}-${index}`}
            >
              {type === "photos" ? (
                <img src={url} alt={`${placeholderLabel} ${index + 1}`} />
              ) : (
                <iframe
                  src={getEmbedUrl(url, type)}
                  className="gallery-media-embed"
                  scrolling="no"
                  title={`${placeholderLabel} ${index + 1}`}
                />
              )}

              <div className="gallery-media-card-overlay">
                <button
                  type="button"
                  onClick={() =>
                    setPreview({
                      src:
                        type === "photos"
                          ? url
                          : getEmbedUrl(url, type),
                      kind,
                      name: `${placeholderLabel} ${index + 1}`,
                    })
                  }
                >
                  <Maximize2 size={17} />
                  Preview
                </button>
                <button
                  type="button"
                  className="gallery-media-remove"
                  onClick={() => removeMedia(type, index)}
                  aria-label={`Remove ${placeholderLabel} ${index + 1}`}
                >
                  <Trash2 size={17} />
                </button>
              </div>
            </article>
          ))}

          {Array.from({
            length: Math.max(visibleEmptySlots - items.length, 0),
          }).map((_, index) => (
            <div
              className={`gallery-placeholder gallery-placeholder-${kind}`}
              key={`${type}-placeholder-${index}`}
            >
              <Icon size={kind === "video" ? 25 : 23} />
              <strong>{placeholderLabel}</strong>
              <span>Empty slot</span>
            </div>
          ))}

          {type === "photos" && (
            <button
              type="button"
              className="gallery-add-card gallery-add-image"
              onClick={() => !isUploading && photoInputRef.current?.click()}
            >
              <span className="gallery-add-icon">
                {isUploading ? (
                  <span className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Uploading...</span>
                  </span>
                ) : (
                  <Plus size={24} />
                )}
              </span>
              <Camera size={22} />
              <strong>{isUploading ? "Uploading..." : "Add photo"}</strong>
              <span>Tap to upload more</span>
            </button>
          )}
        </div>

        {type !== "photos" && (
          <div className="portfolio-url-add">
            <input
              type="url"
              value={type === "reels" ? reelUrl : videoUrl}
              onChange={(e) =>
                type === "reels"
                  ? setReelUrl(e.target.value)
                  : setVideoUrl(e.target.value)
              }
              placeholder={
                type === "reels"
                  ? "Paste Instagram reel URL..."
                  : "Paste YouTube video URL..."
              }
              onKeyDown={(e) => e.key === "Enter" && addLink(type)}
            />
            <button
              type="button"
              className="portfolio-primary-button"
              onClick={() => addLink(type)}
            >
              <Link2 size={16} />
              Add
            </button>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="portfolio-tab-panel portfolio-gallery-panel">
      <input
        type="file"
        ref={photoInputRef}
        onChange={handlePhotoUpload}
        className="portfolio-hidden-input"
        accept="image/*"
      />

      {renderGallerySection(
        "Photos",
        "Show your best portfolio images. You can keep adding more whenever you need.",
        "photos",
        photoPreviews,
        8,
      )}

      {renderGallerySection(
        "Short Reels",
        "Paste Instagram reel links and preview them directly from the card.",
        "reels",
        reels,
        4,
      )}

      {renderGallerySection(
        "Videos",
        "Paste YouTube video links. They open inside the preview modal.",
        "videos",
        videos,
        3,
      )}

      <PortfolioMediaPreview
        preview={preview}
        onClose={() => setPreview(null)}
      />
    </div>
  );
};

export default Gallery;
